import { Entity } from '@core/entities/entity'
import { AuditCheckIn } from './AuditCheckIn'
import { AuditCheckInExtension } from './AuditCheckInExtension'
import { AuditForcedCheckOut } from './AuditForcedCheckOut'
import { OrganisationRole, ProjectRole, User } from '@core/entities/user'
import { plainToInstance, Type } from 'class-transformer'
import { PermitSuspensionReasons } from '@core/entities/permit/PermitSuspension'
import { RegisterItemWithLinksPropertyData } from '@core/entities/register/RegisterItem'
import { GeoJSON } from 'geojson'
import { CommentSecondaryReferenceObjectTypes } from '@core/entities/comments'
import { TemplateQuestion } from '@core/entities/template/TemplateQuestion'
import { ImageWarning } from '@core/entities/ImageWarning'
import {
  IAuditResultMetadata,
  OnboardingFormAuditData,
} from '@core/entities/audit/AuditResult/IAuditResultMetadata'
import { PermissionsUpdatedData } from '@core/entities/audit/AuditResult/PermissionsUpdatedData'
import { PermitStatus } from '@core/entities/permit/PermitBase'
import { ChecklistStatus } from '@core/entities/checklist/Checklist/types'
import { Project, ProjectStatus } from '@core/entities/project'
import {
  PermissionAssignmentType,
  PermissionEntityType,
} from '@core/entities/Groups/GroupsPermissions'
import { Folder } from '@core/entities/folders/Folder'
import { SiteBoxDocument } from '@core/entities/SiteBoxDocument'
import { TaskAuditResultMetadata } from '@core/entities/task/TaskAuditResultMetadata'
import { BriefingSessionAuditMetadata } from '@core/entities/briefing'
import { ApprovalStage } from '@core/entities/permit/PermitApprovalReviews'
import { CompanyComplianceStatus } from '@core/entities/company/CompanyCompliance/types'

export class AuditResultMetadata extends Entity<IAuditResultMetadata> {
  @Type(() => AuditCheckIn)
  checkIns?: Array<AuditCheckIn>
  @Type(() => AuditCheckIn)
  checkOuts?: Array<AuditCheckIn>
  @Type(() => AuditCheckInExtension)
  checkInExtensions?: Array<AuditCheckInExtension>
  @Type(() => AuditForcedCheckOut)
  forcedCheckOuts?: Array<AuditForcedCheckOut>
  forcedCheckOutReason?: string
  mediaUrls?: Array<string>

  attachments?: Array<{
    key: string
    getUrl: string
    downloadUrl: string
    type: string
    mimeType: string
    contentType?: string
  }>

  permitRoleName?: string

  checklistRoleName?: string

  permitGeoJson?: GeoJSON

  distanceToPermitInMeters?: number

  @Type(() => User)
  transferReceiver?: User

  taskChecklistId?: number

  suspensionReasonCategory?: PermitSuspensionReasons

  delta: {
    itemValues: Array<{
      postValue: RegisterItemWithLinksPropertyData
      preValue?: RegisterItemWithLinksPropertyData
    }>
  }

  postValues: {
    itemValues: RegisterItemWithLinksPropertyData[]
  }

  preValues: {
    itemValues: RegisterItemWithLinksPropertyData[]
  }

  updateNote?: string

  inputType?: string

  commentSecondaryReferenceObjectId: number

  commentSecondaryReferenceObjectIdText: string

  commentSecondaryReferenceObjectType: CommentSecondaryReferenceObjectTypes

  @Type(() => TemplateQuestion)
  commentAnsweredQuestionDataResult: TemplateQuestion

  offlineSubmittedOn?: string

  @Type(() => ImageWarning)
  warnings?: Array<ImageWarning>

  emailAddress?: string

  permitExtensionEndsOn?: string

  permitShortenedEndsOn?: string

  permitPreviousEndsOn?: string

  @Type(() => User)
  changedPermitHolderFrom?: User

  @Type(() => User)
  changedPermitHolderTo?: User

  @Type(() => PermissionsUpdatedData)
  permissionsUpdatedData?: PermissionsUpdatedData

  permitStatusBefore?: PermitStatus

  permitStatusAfter?: PermitStatus

  projectStatusBefore?: ProjectStatus

  projectStatusAfter?: ProjectStatus

  permissionAssignmentReferenceObjectId?: number

  permissionAssignmentReferenceObjectName?: string

  permissionAssignmentReferenceObjectType?: PermissionEntityType

  permissionAssignmentAuthGroupIds?: Array<number>

  permissionAssignmentAuthGroupNames?: Array<string>

  permissionAssignmentType?: PermissionAssignmentType

  preProjectMemberData?: {
    roles: Array<ProjectRole>
  }

  postProjectMemberData?: {
    roles: Array<ProjectRole>
  }

  preOrgMemberData?: {
    roles: Array<OrganisationRole>
  }

  postOrgMemberData?: {
    roles: Array<OrganisationRole>
  }

  inspectionChecklist?: {
    checklistId: number
    templateName: string
  }

  inspection?: {
    id: string
    name: string
  }

  scheduledInspectionId?: number

  permitApprovalId?: string

  permitApprovalStage?: ApprovalStage

  permitApprovalLabel?: string

  checklistApprovalLabel?: string

  checklistStatusBefore?: ChecklistStatus

  checklistStatusAfter?: ChecklistStatus

  @Type(() => BriefingSessionAuditMetadata)
  briefedSessionData?: BriefingSessionAuditMetadata

  selfBriefed?: boolean // True for self-briefing (e.g. via QR code)

  briefingText?: string

  @Type(() => Folder)
  movedToFolder?: Folder

  @Type(() => Folder)
  movedFromFolder?: Folder

  @Type(() => SiteBoxDocument)
  preSiteBoxDocumentValuesData?: Partial<SiteBoxDocument>

  @Type(() => SiteBoxDocument)
  postSiteBoxDocumentValuesData?: Partial<SiteBoxDocument>

  @Type(() => Project)
  projectDataAfter?: Project

  @Type(() => Project)
  projectDataBefore?: Project

  @Type(() => TaskAuditResultMetadata)
  taskPreValuesData?: TaskAuditResultMetadata
  @Type(() => TaskAuditResultMetadata)
  taskPostValuesData?: TaskAuditResultMetadata

  taskRequestChangesComment?: string

  taskReassignedNotes?: string

  taskCancelledNotes?: string

  taskReopenReason?: string

  taskAddressedRequestedChanges?: boolean

  @Type(() => User)
  nominatedApprovers: Array<User>

  nominatedForApproval: string

  movedFromAnnotationSet?: {
    id: number
    diagramName: string
  }

  movedToAnnotationSet?: {
    id: number
    diagramName: string
  }

  linkedToPermit?: {
    id: number
    organisationId: number
    uuid: string
    project: {
      id: number
      name: string
    }
    summary?: string
    template: {
      id: number
      name: string
    }
  }

  preFeatureFlags: {
    [flag: string]: {
      enabled: boolean
    }
  }

  postFeatureFlags: {
    [flag: string]: {
      enabled: boolean
    }
  }

  companyInputType?: 'MANUAL' | 'CSV_UPLOAD'

  preCompanyDataValues?: {
    name: string
    code: string
  }

  postCompanyDataValues?: {
    name: string
    code: string
  }

  orgRole?: OrganisationRole

  preOnboardingFormData?: OnboardingFormAuditData

  postOnboardingFormData?: OnboardingFormAuditData

  // specific to company compliance
  complianceTitle?: string

  complianceExpiryDate?: string

  complianceRejectionReason?: string

  complianceStatusBefore?: CompanyComplianceStatus

  complianceStatusAfter?: CompanyComplianceStatus

  // specific to user competencies — see api/docs/domains/competencies.md §7.2.
  // `competencySubmissionId` is set only on USER_COMPETENCY_COMPLETED.
  competencyTitle?: string

  competencySubmissionId?: number

  public static new(payload: IAuditResultMetadata): AuditResultMetadata {
    const entity = plainToInstance(AuditResultMetadata, payload)

    return entity
  }
}
